import type { Density } from "@/components/desktop/DesktopInventoryTable";

/**
 * Shared responsive column and cell-width math for the inventory grids (#571, #568).
 *
 * The phone and desktop grids both derive `columns` (how many `target`-wide cells fit the padded
 * width, clamped) and then a `cellWidth` split evenly across them, so a partial last row never
 * stretches its cells. `desktopGridColumns` and `mobileGridColumns` pass their own padding, gap,
 * target and clamp to `gridColumns`.
 */

export type GridLayout = { columns: number; cellWidth: number; };

/** Derives the column count and cell width for either inventory grid. */
export function gridColumns(opts: {
  /** Total available width (measured content width on desktop; window width on phone). */
  width: number;
  /** Horizontal padding on each side of the grid. */
  hPad: number;
  /** Between-cell gap. */
  gap: number;
  /** Preferred cell width; the actual width is derived from the resulting column count. */
  target: number;
  minColumns: number;
  maxColumns: number;
  /** Whether the fit accounts for the inter-cell gaps. Desktop does; the phone grid does not
   *  (`floor(usable/target)`), which fits one extra column around 414px wide. The phone behavior is
   *  kept so sharing the math changes no layout. */
  countGaps: boolean;
}): GridLayout {
  const usable = Math.max(0, opts.width - opts.hPad * 2);
  const raw = opts.countGaps
    ? Math.floor((usable + opts.gap) / (opts.target + opts.gap))
    : Math.floor(usable / opts.target);
  const columns = Math.min(opts.maxColumns, Math.max(opts.minColumns, raw));
  const cellWidth = Math.max(0, (usable - opts.gap * (columns - 1)) / columns);
  return { columns, cellWidth };
}

// Desktop grid (#568): keyed on the measured content width. Density picks the target cell width,
// and the column clamp keeps cells from ballooning on a narrow pane or shrinking to nothing on an
// ultra-wide monitor.
export const DESKTOP_GRID_H_PAD = 24; // Matches the table's `px-6` gutter.
export const DESKTOP_GRID_GAP = 12; // Applies on both axes.
export const MIN_DESKTOP_GRID_COLS = 2;
export const MAX_DESKTOP_GRID_COLS = 8;

// Target cell width per density; the actual width is derived from the resulting column count.
const TARGET_CELL_WIDTH: Record<Density, number> = { comfortable: 160, compact: 128 };

export function desktopGridColumns(contentWidth: number, density: Density): GridLayout {
  return gridColumns({
    width: contentWidth,
    hPad: DESKTOP_GRID_H_PAD,
    gap: DESKTOP_GRID_GAP,
    target: TARGET_CELL_WIDTH[density],
    minColumns: MIN_DESKTOP_GRID_COLS,
    maxColumns: MAX_DESKTOP_GRID_COLS,
    countGaps: true,
  });
}

// Phone grid: keyed on the window width, with 150px target cells and 2-4 columns.
// `MOBILE_GRID_H_PAD` matches the list's `p-4` (16px) gutter and `MOBILE_GRID_GAP` the
// `columnWrapperStyle` gap.
export const MOBILE_GRID_H_PAD = 16;
export const MOBILE_GRID_GAP = 8;
export const MIN_MOBILE_GRID_COLS = 2;
export const MAX_MOBILE_GRID_COLS = 4;
const MOBILE_TARGET_CELL_WIDTH = 150;

// Source prices and the unit's cost/Market/gain need a readable two-up phone cell. Tablets still
// fit more columns; a third phone column clips prices and wraps money figures.

export function mobileGridColumns(width: number): GridLayout {
  return gridColumns({
    width,
    hPad: MOBILE_GRID_H_PAD,
    gap: MOBILE_GRID_GAP,
    target: MOBILE_TARGET_CELL_WIDTH,
    minColumns: MIN_MOBILE_GRID_COLS,
    maxColumns: MAX_MOBILE_GRID_COLS,
    countGaps: false,
  });
}

// Gutters without `columnWrapperStyle` (TRO-864).
//
// FlashList has no `columnWrapperStyle` and positions cells with its own layout manager, so a `gap`
// on the content container draws nothing between them. Both gutters come from padding every cell
// identically, which works under any layout engine and survives recycling, unlike an index-derived
// `marginRight` on all but the last column.
//
// Each cell gets half the gutter on each side, and the container subtracts the half that would
// otherwise double at the outer edge:
//
//   outer edge   = (pad - gap/2) + gap/2 = pad          unchanged
//   between two  =       gap/2   + gap/2 = gap          unchanged
//   cell content = column width - gap                   unchanged (see below)
//
// The content width equals `cellWidth`, so the layout is unchanged:
//
//   column width  = (width - 2*(pad - gap/2)) / columns = (width - 2*pad + gap) / columns
//   minus the gap = (width - 2*pad + gap - gap*columns) / columns
//                 = (width - 2*pad - gap*(columns - 1)) / columns = cellWidth
//
// In list mode the column gutter is zero and only the row gutter remains, so the two gaps are
// separate arguments.
export type GridGutters = {
  /** Horizontal padding for the list's content container. */
  contentHPad: number;
  /** Vertical padding for the list's content container. */
  contentVPad: number;
  /** Horizontal padding on every cell: half a column gutter. */
  cellHPad: number;
  /** Vertical padding on every cell: half a row gutter. */

  cellVPad: number;
};

export function gridGutters(
  opts: { hPad: number; vPad: number; colGap: number; rowGap: number; },
): GridGutters {
  return {
    contentHPad: opts.hPad - opts.colGap / 2,
    contentVPad: opts.vPad - opts.rowGap / 2,
    cellHPad: opts.colGap / 2,
    cellVPad: opts.rowGap / 2,
  };
}
